import {
  Cancel01Icon,
  InformationCircleIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { instance } from "api/apiBase";
import {
  BaseInput,
  Button,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components";
import { moneyCentsParser } from "shared/components/forms/BaseInput/parsers";
import { Modal } from "shared/components/Modal";
import { useFormTyped } from "shared/hooks";
import { getFullName } from "shared/utils/get-full-name";
import { Applicant } from "types/application";
import { getApplicantRole } from "utils/getApplicantRole";
import { VerificationNames } from "shared/types";
import { getVerificationQueryKeys } from "views/applications-id-report/utils/getVerificationQueryKeys";

export const EditHousingAssistanceModal = ({
  opened,
  onClose,
  applicant,
  amountCents,
  verificationId
}: {
  opened: boolean;
  onClose: () => void;
  applicant: Applicant;
  amountCents: number;
  verificationId?: number;
}) => {
  const queryClient = useQueryClient();
  const form = useFormTyped({
    assistance: amountCents
  });

  const mutation = useMutation({
    mutationKey: ["EDIT_MONTHLY_ASSISTANCE", applicant.id],
    mutationFn: (data: { assistance: number }) => {
      const endpoint = `/portal/v1/applications/${applicant.application_id}/applicants/${applicant.id}/housing_assistance`;
      return instance
        .patch(endpoint, {
          voucher_amount: {
            cents: data.assistance,
            currency: "USD"
          }
        })
        .then(({ data }) => data);
    },
    onSuccess: () => {
      toast.success("Monthly assistance was successfully updated");
      queryClient.invalidateQueries({
        queryKey: getVerificationQueryKeys({
          reportName: VerificationNames.PetScreeningReport,
          applicationId: Number(applicant.application_id || 0),
          applicantId: Number(applicant.id || 0),
          verificationId
        })
      });
    },
    onError: (error) => {
      toast.error(error.message);
    }
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      panelClassName="max-w-[477px] w-full"
    >
      <form
        onSubmit={form.onSubmit({
          onSuccess: (values) => {
            mutation.mutate(
              {
                assistance: values.assistance
              },
              {
                onSuccess: onClose
              }
            );
          }
        })}
      >
        <header className="px-6 py-5 text-lg font-bold flex justify-between items-center border-b border-alpha-100">
          Edit monthly assistance
          <IconButton
            HugeIcon={Cancel01Icon}
            size={IconButtonSize.SM}
            variant={IconButtonVariant.TEXT_BASE}
            onClick={onClose}
          />
        </header>
        <div className="px-6 py-4 bg-warning-50 border-b border-alpha-100 flex gap-4 items-center">
          <HugeiconsIcon
            icon={InformationCircleIcon}
            size={24}
            className="text-warning-700 shrink-0"
          />
          <p className="text-sm text-warning-700 font-medium">
            Please note that editing this income will lead to an application
            score recalculation. Decision recommendations may change.
          </p>
        </div>
        <div className="px-6 py-5 space-y-1 border-b border-alpha-100">
          <h2 className="text-base font-medium text-content-1000">
            {getFullName({
              firstName: applicant.first_name,
              lastName: applicant.last_name,
              middleName: applicant.middle_name,
              short: true
            })}
          </h2>

          <p className="text-sm text-content-400 font-medium">
            {getApplicantRole(applicant.role)}
          </p>
        </div>

        <div className="bg-background-900 p-6">
          <BaseInput
            parser={moneyCentsParser()}
            id="money"
            label="Monthly assistance"
            prefix="$"
            placeholder="0.00"
            required
            {...form.register("assistance")}
          />
        </div>

        <div className="px-6 py-4 flex gap-2 justify-end border-t border-alpha-100">
          <Button className="w-[125px]" onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="w-[125px]"
            variant={ButtonVariant.PRIMARY}
            type="submit"
            loading={mutation.isPending}
          >
            Save changes
          </Button>
        </div>
      </form>
    </Modal>
  );
};
